/**
 * Wire number formats and caller-ID presentation (design §22.5.1, DD8-19, R-OUT-3, R-TRK-2).
 *
 * Numbers are E.164 inside WeTalk; each trunk states how its provider writes them, and nothing is
 * guessed. Every function is pure and total over its declared domain, and throws `RangeError`
 * outside it — a number that cannot be written in the trunk's format is a refusal the caller must
 * handle, never a best effort that rings someone else.
 *
 * | format | `+302113331391` on the wire |
 * |---|---|
 * | `e164_plus` | `+302113331391` |
 * | `e164` | `302113331391` |
 * | `international_00` | `00302113331391` |
 * | `national` (GR, no trunk prefix) | `2113331391` |
 */
import { NATIONAL_NUMBER_RULE, nationalNumberRuleOf } from "./national-number-rule.js";
/** ITU-T E.164 as WeTalk stores it: `+`, a non-zero first digit, 7–15 digits in all. */
export const E164_PATTERN = /^\+[1-9][0-9]{6,14}$/;
export function isE164(value) {
    return E164_PATTERN.test(value);
}
function assertE164(e164, where) {
    if (!isE164(e164))
        throw new RangeError(`${where}: not an E.164 number`);
}
function ruleOrThrow(country, where) {
    if (country === null)
        throw new RangeError(`${where}: the national form needs a national country`);
    const rule = nationalNumberRuleOf(country);
    if (rule === undefined)
        throw new RangeError(`${where}: ${country} has no reviewed national number rule`);
    return rule;
}
/** The national significant number of `e164` under `rule`, or a `RangeError` when it is not one. */
function nationalSignificantNumber(e164, rule, where) {
    const prefix = `+${rule.callingCode}`;
    if (!e164.startsWith(prefix))
        throw new RangeError(`${where}: the number is not in the national country`);
    const nsn = e164.slice(prefix.length);
    if (nsn.length !== rule.nationalSignificantLength) {
        throw new RangeError(`${where}: the national significant number has the wrong length`);
    }
    return nsn;
}
/** E.164 → how the trunk's provider wants it dialled (and how it writes its DIDs). */
export function toWireNumber(e164, format, nationalCountry) {
    assertE164(e164, "toWireNumber");
    switch (format) {
        case "e164_plus":
            return e164;
        case "e164":
            return e164.slice(1);
        case "international_00":
            return `00${e164.slice(1)}`;
        case "national": {
            const rule = ruleOrThrow(nationalCountry, "toWireNumber");
            return `${rule.trunkPrefix}${nationalSignificantNumber(e164, rule, "toWireNumber")}`;
        }
    }
}
/**
 * Every common way a provider may write `e164` as the number it delivers a call to (2026-10-04, design §22.29
 * AS AMENDED): the three international forms, and — for each reviewed country the number belongs to (its calling
 * code and its national significant length, `NATIONAL_NUMBER_RULE`) — the national significant number, plus the
 * trunk prefix and the NSN where the country has one. Sorted, distinct. Only the registration front reads it: a
 * provider may deliver in one format and expect another when dialled, and one trunk setting cannot say both.
 *
 * | `+302113331391` | `+302113331391`, `302113331391`, `00302113331391`, `2113331391` (GR, no trunk prefix) |
 */
export function commonWireFormsOf(e164) {
    return commonWireFormsUnder(e164, NATIONAL_NUMBER_RULE);
}
/** {@link commonWireFormsOf} under the given rules. Exported for its test (no reviewed country has a trunk prefix yet). */
export function commonWireFormsUnder(e164, ruleOfCountry) {
    assertE164(e164, "commonWireFormsOf");
    const out = new Set([e164, e164.slice(1), `00${e164.slice(1)}`]);
    for (const rule of Object.values(ruleOfCountry)) {
        const prefix = `+${rule.callingCode}`;
        if (!e164.startsWith(prefix) || e164.length - prefix.length !== rule.nationalSignificantLength)
            continue;
        const nsn = nationalSignificantNumber(e164, rule, "commonWireFormsOf");
        out.add(nsn);
        if (rule.trunkPrefix !== "")
            out.add(`${rule.trunkPrefix}${nsn}`);
    }
    return Object.freeze([...out].sort());
}
const DIGITS = /^[0-9]+$/;
/** The provider's wire form → E.164. Strict: a wire number in another format than the trunk's is refused. */
export function fromWireNumber(wire, format, nationalCountry) {
    let e164;
    switch (format) {
        case "e164_plus":
            e164 = wire;
            break;
        case "e164":
            if (!DIGITS.test(wire))
                throw new RangeError("fromWireNumber: an e164 wire number is digits only");
            e164 = `+${wire}`;
            break;
        case "international_00":
            if (!DIGITS.test(wire) || !wire.startsWith("00")) {
                throw new RangeError("fromWireNumber: an international_00 wire number starts 00 and is digits only");
            }
            e164 = `+${wire.slice(2)}`;
            break;
        case "national": {
            const rule = ruleOrThrow(nationalCountry, "fromWireNumber");
            if (!DIGITS.test(wire) || !wire.startsWith(rule.trunkPrefix)) {
                throw new RangeError("fromWireNumber: a national wire number is digits only");
            }
            const nsn = wire.slice(rule.trunkPrefix.length);
            if (nsn.length !== rule.nationalSignificantLength) {
                throw new RangeError("fromWireNumber: the national significant number has the wrong length");
            }
            e164 = `+${rule.callingCode}${nsn}`;
            break;
        }
    }
    assertE164(e164, "fromWireNumber");
    return e164;
}
/**
 * The From user of an outbound INVITE. `account_name` presents the SIP account's auth username —
 * the provider shows the account's number from it (modulus, DD8-19).
 */
export function callerIdOnWire(e164, presentation, nationalCountry, authUsername) {
    switch (presentation) {
        case "number_e164_plus":
            assertE164(e164, "callerIdOnWire");
            return e164;
        case "number_national":
            return toWireNumber(e164, "national", nationalCountry);
        case "account_name":
            assertE164(e164, "callerIdOnWire");
            if (authUsername.length === 0)
                throw new RangeError("callerIdOnWire: account_name needs the auth username");
            return authUsername;
    }
}
//# sourceMappingURL=wire-number.js.map